import { siteDetailPath } from '@/lib/siteDashboard';
import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useDashboardAnomalies } from '@/hooks/useDashboardAnomalies';
import { rowKey, rowLink, rowTitle } from '@/lib/anomalyView';
import styles from '../DashboardPage.module.css';

/**
 * Anomaly alerts widget. Reads /dashboard/anomalies (60s poll +
 * 30s server cache) and renders up to 5 critical/warning rows: a
 * charger row opens its charger, a site row (a whole site down is one,
 * addendum item 3, 28 Oct 2026) opens its site. The rules are
 * apps/api lib/anomalies; the full list is the /anomalies page.
 */
export default function AnomaliesWidget({ onNavigate }: { onNavigate?: (to: string) => void }) {
  const isSite = useSiteDashboard();
  const q = useDashboardAnomalies();
  const data = q.data;
  const total = data?.total_count ?? 0;
  const critical = data?.rows.some((r) => r.severity === 'critical') ?? false;

  return (
    <div className={styles.chartBody}>
      <div className={styles.chartHeader}>
        <div className={styles.chartIcon}><AlertTriangle size={14} /></div>
        <p className={styles.chartTitle}>Anomalies</p>
        {total > 0 && (
          <span
            className={`${styles.anomCountBadge} ${!critical ? styles.anomCountBadgeQuiet : ''}`}
          >
            {total}
          </span>
        )}
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className={styles.anomEmpty}>
          <span className={styles.anomEmptyIcon}><CheckCircle2 size={18} /></span>
          <p>All chargers operating normally.</p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <>
          <div className={styles.anomList}>
            {data.rows.map((row) => (
              <a
                key={rowKey(row)}
                href={rowLink(row, isSite, siteDetailPath)}
                onClick={(e) => {
                  // Internal navigation via App's pushState wrapper —
                  // avoids a full page reload. Fall through to the
                  // default <a> behavior if onNavigate isn't wired
                  // (defensive; shouldn't happen in normal app shell).
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate(rowLink(row, isSite, siteDetailPath));
                  }
                }}
                className={styles.anomRow}
              >
                <span
                  className={`${styles.anomDot} ${row.severity === 'critical' ? styles.anomDotCritical : styles.anomDotWarning}`}
                />
                <div className={styles.anomBody}>
                  <span className={styles.anomTitleLine}>
                    {rowTitle(row).title}
                    {row.scope !== 'site' && row.location_name ? ` · ${row.location_name}` : ''}
                  </span>
                  <span className={styles.anomDescLine}>{row.description}</span>
                </div>
                <span className={styles.anomViewBadge}>View</span>
              </a>
            ))}
          </div>
          {total > data.rows.length && (
            <p className={styles.anomFooter}>
              <a
                className={styles.anomFooterLink}
                href={isSite ? '/my-site' : '/anomalies'}
                onClick={(e) => {
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate(isSite ? '/my-site' : '/anomalies');
                  }
                }}
              >
                {isSite ? 'View My Sites' : `View all ${total}`}
              </a>
            </p>
          )}
        </>
      )}
    </div>
  );
}
